import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Link } from "../../../components/Link";
import { FinancingModalTriggerTheme } from "../../../constants";
import { WebPageURL, isoMonths } from "../../../models/nominals";
import { FinancingPlanMeta } from "../../../utils/financing";
import { formatFinancingPlan } from "../../../utils/format";
import { usePageURL } from "../../../utils/urls";

import styles from "./Copy.module.scss";

export const Copy = (
    props: Immutable<{
        plan: FinancingPlanMeta;
        theme?: FinancingModalTriggerTheme;
        aprInfoID?: string | null;
    }>,
) => {
    const plan = formatFinancingPlan(props.plan);
    const financeLinkURL = usePageURL("finance_link");
    const homeURL = usePageURL("home");

    if (props.theme === FinancingModalTriggerTheme.SPECIAL) {
        return (
            <div>
                <h2>{t`Special Financing Offers Available`}</h2>
                <p>{t`buy today and pay over time with approved credit`}</p>
            </div>
        );
    }

    const financeLink = financeLinkURL || homeURL;
    const aprInfoURL = props.aprInfoID
        ? `${financeLink}${props.aprInfoID}`
        : financeLink;
    if (
        props.theme === FinancingModalTriggerTheme.MINI_FINANCING ||
        props.theme === FinancingModalTriggerTheme.CARD_OVERLAP
    ) {
        const useMiniFinancingText =
            props.theme === FinancingModalTriggerTheme.MINI_FINANCING;
        const offerCopy = useMiniFinancingText
            ? interpolate(t`%(apr)s APR for %(length)s Months`, plan)
            : interpolate(t`Enjoy %(apr)s Interest for %(length)s Years`, {
                  ...plan,
                  length: (isoMonths.unwrap(props.plan.length) / 12).toString(),
              });
        const subCopy = useMiniFinancingText
            ? t`Check if you pre-qualify without affecting your credit score.`
            : interpolate(t`on purchases %(threshold)s or more.`, plan);
        return (
            <div>
                <h2>
                    {offerCopy}
                    {props.plan.superscript && (
                        <sup>
                            <Link href={aprInfoURL as WebPageURL}>
                                {props.plan.superscript}
                                <span className="ada-screenreader-only">
                                    {t`Additional information about Terms and Conditions`}
                                </span>
                            </Link>
                        </sup>
                    )}
                </h2>
                {useMiniFinancingText ? (
                    <h3 className={styles.preQualifyHeading}>{subCopy}</h3>
                ) : (
                    <p>{subCopy}</p>
                )}
            </div>
        );
    }
    return (
        <>
            <div className={styles.terms}>
                <h2>
                    <div
                        className={concatClassNames([
                            styles.termsLine,
                            styles.termsLine1,
                        ])}
                    >
                        {interpolate(t`%(apr)s APR`, plan)}
                    </div>
                    <div
                        className={concatClassNames([
                            styles.termsLine,
                            styles.termsLine2,
                        ])}
                    >
                        {t`for`} <div className={styles.lineRule}></div>
                    </div>
                    <div
                        className={concatClassNames([
                            styles.termsLine,
                            styles.termsLine3,
                        ])}
                    >
                        {interpolate(t`%(length)s Months`, plan)}
                        <sup>
                            <Link href={financeLink}>
                                {plan.superscript}
                                <span className="ada-screenreader-only">
                                    {t`Additional information about Terms and Conditions`}
                                </span>
                            </Link>
                        </sup>
                    </div>
                </h2>
            </div>
            <div className={concatClassNames([styles.see, styles.seeBlock])}>
                <div className={styles.seeBold}>
                    {t`Check if you're pre-qualified`}
                </div>
                <div className={styles.seeSmall}>
                    {t`without affecting your credit score.`}
                </div>
            </div>
        </>
    );
};
